import { firestoreFieldValueArrayUnion, firestoreFieldValueIncrement, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { guid } from "@buildwithflux/shared";
import { UserPrivateMetadataRepository } from "./UserPrivateMetadataRepository";
export class FirestoreUserPrivateMetadataRepository extends UserPrivateMetadataRepository {
    db;
    firestoreUpmChangeSubscriptions = new Map();
    constructor(db) {
        super();
        this.db = db;
    }
    /** @inheritDoc */
    async touch(userUid) {
        const emptyUserPrivateMetadata = { uid: userUid };
        await this.db.userPrivateMetadata(userUid).set(emptyUserPrivateMetadata, { merge: true });
        return emptyUserPrivateMetadata;
    }
    /** @inheritDoc */
    async getForUser(userUid) {
        return oneFromSnapshot(await this.db.userPrivateMetadata(userUid).get(), () => undefined);
    }
    /** @inheritDoc */
    async update(userUid, userPrivateMetadata) {
        await this.db.userPrivateMetadata(userUid).set({ uid: userUid, ...userPrivateMetadata }, { merge: true });
    }
    /** @inheritDoc */
    async markOnboardingStepsComplete(userUid, steps) {
        await this.db.userPrivateMetadata(userUid).update({
            uid: userUid,
            "onboarding.completedSteps": (await firestoreFieldValueArrayUnion(this.db))(...steps),
        });
    }
    /** @inheritDoc */
    async updateSubscription(userUid, customerUid, keyToUpdate, updatedSubscriptionInformation) {
        await this.db.userPrivateMetadata(userUid).update({
            uid: userUid,
            [`payment.${keyToUpdate}.${updatedSubscriptionInformation.uid}`]: updatedSubscriptionInformation,
            "payment.customerUid": customerUid,
        });
    }
    /** @inheritDoc */
    async deleteForUser(userUid) {
        await this.db.userPrivateMetadata(userUid).delete();
    }
    /** @inheritDoc */
    subscribeForUser(userUid, onChange) {
        const alreadySubscribing = this.firestoreUpmChangeSubscriptions.has(userUid);
        if (!alreadySubscribing) {
            const upmRef = this.db.userPrivateMetadata(userUid);
            const unsubscriber = upmRef.onSnapshot((snap) => {
                const upmSnap = snap.data();
                if (upmSnap) {
                    this.notifyOnChange(userUid, { type: "updated", data: upmSnap });
                }
            });
            this.firestoreUpmChangeSubscriptions.set(userUid, unsubscriber);
        }
        return this.subscriptionManager.addSubscription(userUid, onChange, () => {
            const existingSubCount = this.subscriptionManager.listenerCount(userUid);
            if (existingSubCount === 0) {
                const firestoreUnsub = this.firestoreUpmChangeSubscriptions.get(userUid);
                if (firestoreUnsub) {
                    firestoreUnsub();
                    this.firestoreUpmChangeSubscriptions.delete(userUid);
                }
            }
        });
    }
    /** @inheritDoc */
    async createApiKeyId(userUid) {
        // Pre-set in case the UPM doesn't exist yet
        await this.db.userPrivateMetadata(userUid).set({ uid: userUid }, { merge: true });
        const id = guid();
        await this.db.userPrivateMetadata(userUid).update({
            "api.keys": (await firestoreFieldValueArrayUnion(this.db))({ id, createdAt: Date.now() }),
        });
        return id;
    }
    /** @inheritDoc */
    async dismissIntercomChat(userUid) {
        await this.db.userPrivateMetadata(userUid).update({
            uid: userUid,
            "intercomChat.dismissalCount": await firestoreFieldValueIncrement(this.db),
            "intercomChat.lastDismissalAt": Date.now(),
        });
    }
    /** @inheritDoc */
    async setLearningProjectVisibility(userUid, hide) {
        await this.db.userPrivateMetadata(userUid).update({ "learningProjects.hide": hide });
    }
    /** @inheritDoc */
    async setLearningProjectUid(userUid, fromProjectUid, clonedProjectUid) {
        const clonedProject = {
            fromProjectUid,
            clonedProjectUid,
            clonedOn: Date.now(),
        };
        await this.db.userPrivateMetadata(userUid).update(`learningProjects.projects.${fromProjectUid}`, clonedProject);
    }
    /** @inheritDoc */
    async dismissAnnouncement(userUid, announcementKey) {
        const updates = {
            [`announcement.dismissed.${announcementKey}`]: true,
        };
        // Backward compatibility for old announcement key
        if (announcementKey === "autoLayout") {
            updates["announcement.autoLayoutViewed"] = true;
        }
        await this.db.userPrivateMetadata(userUid).update(updates);
    }
    /** @inheritDoc */
    async updatePaymentMethodStatus(userUid, subscriptionId, status) {
        await this.db.userPrivateMetadata(userUid).update({
            [`payment.copilotCreditSubscriptions.${subscriptionId}.paymentMethodStatus`]: status,
        });
    }
}
//# sourceMappingURL=FirestoreUserPrivateMetadataRepository.js.map